import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { RecordNavigationComponent } from '../_shared/record-navigation/record-navigation.component';
import { ExpectedDeliveryService } from '../../_services/expected-delivery.service';
import { ExpectedDelivery, ExpectedDeliveryReceipt } from '../../_models/expected-delivery.interface';
import { DatePipe } from '@angular/common';

interface TrackingEntry {
  carrier: string;
  number: string;
}

@Component({
  selector: 'hawk-expected-deliveries-show',
  standalone: true,
  imports: [
    RecordNavigationComponent,
    DatePipe,
    FormsModule,
    RouterLink,
  ],
  templateUrl: './expected-deliveries-show.component.html',
  host: {
    class: 'app-page'
  }
})
export class ExpectedDeliveriesShowComponent implements OnInit {
  delivery: ExpectedDelivery | null = null;
  history: ExpectedDeliveryReceipt[] = [];
  isLoading = true;
  errorMessage = '';
  showCancelConfirm = false;
  showDeleteConfirm = false;
  selectedReceipt: ExpectedDeliveryReceipt | null = null;

  // Tracking Numbers
  carriers = ['DHL', 'Hermes', 'UPS', 'DPD', 'GLS', 'FedEx', 'Andere'];
  isEditingTracking = false;
  isSavingTracking = false;
  trackingNumbers: TrackingEntry[] = [];

  // E-Mail Benachrichtigungen
  isEditingNotifications = false;
  isSavingNotifications = false;
  editInternalEmails = '';
  editExternalEmails = '';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private expectedDeliveryService: ExpectedDeliveryService
  ) {}

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.loadDelivery(id);
      this.loadHistory(id);
    }
  }

  private loadDelivery(id: string): void {
    this.expectedDeliveryService.getById(id).subscribe({
      next: (data) => {
        this.delivery = data;
        this.isLoading = false;
      },
      error: (err) => {
        this.errorMessage = 'Lieferung konnte nicht geladen werden.';
        this.isLoading = false;
        console.error('Fehler:', err);
      }
    });
  }

  private loadHistory(id: string): void {
    this.expectedDeliveryService.getHistory(id).subscribe({
      next: (data) => {
        this.history = data;
      },
      error: (err) => {
        console.error('Fehler beim Laden der Historie:', err);
      }
    });
  }

  getStatusBadgeClass(status: string): string {
    const classMap: Record<string, string> = {
      'open': 'badge-info',
      'partial': 'badge-warning',
      'completed': 'badge-success',
      'cancelled': 'badge-error',
    };
    return classMap[status] || 'badge-ghost';
  }

  getStatusLabel(status: string): string {
    const labelMap: Record<string, string> = {
      'open': 'Offen',
      'partial': 'Teilweise',
      'completed': 'Abgeschlossen',
      'cancelled': 'Storniert',
    };
    return labelMap[status] || status;
  }

  getOwnerLabel(owner: string): string {
    return owner === 'BENTOMAX' ? 'Bentomax' : 'Kunde';
  }

  getPackagingLabel(type: string | null): string {
    if (!type) return '-';
    return type === 'PALLET' ? 'Palette' : 'Paket';
  }

  cancelDelivery(): void {
    if (!this.delivery) return;

    this.expectedDeliveryService.cancel(this.delivery.id).subscribe({
      next: () => {
        this.showCancelConfirm = false;
        this.loadDelivery(this.delivery!.id.toString());
      },
      error: (err) => {
        this.errorMessage = err.error?.error || 'Fehler beim Stornieren.';
        this.showCancelConfirm = false;
      }
    });
  }

  deleteDelivery(): void {
    if (!this.delivery) return;

    this.expectedDeliveryService.delete(this.delivery.id).subscribe({
      next: () => {
        this.router.navigate(['/lager/erwartete-lieferungen']);
      },
      error: (err) => {
        this.errorMessage = err.error?.error || 'Fehler beim Löschen.';
        this.showDeleteConfirm = false;
      }
    });
  }

  canDelete(): boolean {
    if (!this.delivery) return false;
    // Nur löschen wenn keine Wareneingänge gebucht wurden
    return this.delivery.total_received === 0;
  }

  // Tracking Numbers Management

  /**
   * Parst den external_delivery_note_number String zu TrackingEntry Array
   * Format: "DHL:12345;Hermes:67890"
   */
  parseTrackingNumbers(value: string | null | undefined): TrackingEntry[] {
    if (!value) return [];
    return value.split(';')
      .map(entry => entry.trim())
      .filter(entry => entry.length > 0)
      .map(entry => {
        const colonIndex = entry.indexOf(':');
        if (colonIndex > 0) {
          return {
            carrier: entry.substring(0, colonIndex),
            number: entry.substring(colonIndex + 1)
          };
        }
        // Fallback für alte Daten ohne Carrier
        return { carrier: 'Andere', number: entry };
      });
  }

  /**
   * Formatiert TrackingEntry Array zu String
   */
  formatTrackingNumbers(): string {
    return this.trackingNumbers
      .filter(entry => entry.number?.trim())
      .map(entry => `${entry.carrier}:${entry.number.trim()}`)
      .join(';');
  }

  /**
   * Startet den Bearbeitungsmodus für Tracking-Nummern
   */
  startEditingTracking(): void {
    this.trackingNumbers = this.parseTrackingNumbers(this.delivery?.external_delivery_note_number);
    this.isEditingTracking = true;
  }

  /**
   * Bricht die Bearbeitung ab
   */
  cancelEditingTracking(): void {
    this.isEditingTracking = false;
    this.trackingNumbers = [];
  }

  /**
   * Fügt eine neue Tracking-Nummer hinzu
   */
  addTrackingNumber(): void {
    this.trackingNumbers.push({ carrier: 'DHL', number: '' });
  }

  /**
   * Entfernt eine Tracking-Nummer
   */
  removeTrackingNumber(index: number): void {
    this.trackingNumbers.splice(index, 1);
  }

  /**
   * Speichert die Tracking-Nummern
   */
  saveTrackingNumbers(): void {
    if (!this.delivery) return;

    this.isSavingTracking = true;
    const formattedValue = this.formatTrackingNumbers();

    this.expectedDeliveryService.update(this.delivery.id, {
      external_delivery_note_number: formattedValue
    }).subscribe({
      next: (response) => {
        this.delivery = response.expected_delivery;
        this.isEditingTracking = false;
        this.isSavingTracking = false;
        this.trackingNumbers = [];
      },
      error: (err) => {
        this.errorMessage = err.error?.errors?.join(', ') || 'Fehler beim Speichern.';
        this.isSavingTracking = false;
      }
    });
  }

  /**
   * Gibt die geparsten Tracking-Nummern für die Anzeige zurück
   */
  getDisplayTrackingNumbers(): TrackingEntry[] {
    return this.parseTrackingNumbers(this.delivery?.external_delivery_note_number);
  }

  // E-Mail Benachrichtigungen Management

  /**
   * Startet den Bearbeitungsmodus für E-Mail-Benachrichtigungen
   */
  startEditingNotifications(): void {
    this.editInternalEmails = this.delivery?.internal_notification_emails || '';
    this.editExternalEmails = this.delivery?.external_notification_emails || '';
    this.isEditingNotifications = true;
  }

  /**
   * Bricht die Bearbeitung ab
   */
  cancelEditingNotifications(): void {
    this.isEditingNotifications = false;
    this.editInternalEmails = '';
    this.editExternalEmails = '';
  }

  /**
   * Speichert die E-Mail-Benachrichtigungen
   */
  saveNotifications(): void {
    if (!this.delivery) return;

    this.isSavingNotifications = true;

    this.expectedDeliveryService.update(this.delivery.id, {
      internal_notification_emails: this.editInternalEmails || undefined,
      external_notification_emails: this.editExternalEmails || undefined
    }).subscribe({
      next: (response) => {
        this.delivery = response.expected_delivery;
        this.isEditingNotifications = false;
        this.isSavingNotifications = false;
      },
      error: (err) => {
        this.errorMessage = err.error?.error || err.error?.errors?.join(', ') || 'Fehler beim Speichern.';
        this.isSavingNotifications = false;
      }
    });
  }

  // Artikel Modal

  /**
   * Öffnet das Modal mit der Artikel-Liste für einen Wareneingang
   */
  showArticlesModal(receipt: ExpectedDeliveryReceipt): void {
    this.selectedReceipt = receipt;
  }
}
